/**
 * 账户录入 / 编辑表单（**受控组件** · B 域 B1 · 依据 B 域 PRD §5.1.5 字段表 · §5.2 信用卡）
 *
 * ## 🔴 为什么是"受控 + 内联 Card"，而不是自带状态 + Dialog
 *
 * 与 `PolicyForm` 完全同因（那三个理由在这里逐条成立）：
 * | 选择 | 理由 |
 * |---|---|
 * | **受控**（值在外面） | 表单与"草稿→请求"的映射（`features/account-draft.ts`）分开 ⇒ 后者可用**零依赖基座**直接断言，不必渲染 |
 * | 🔴 **内联 Card（不用 Dialog）** | MUI `Dialog` 走 `Portal`，在 `renderToStaticMarkup` 下内容是否进产物**取决于实现细节** ⇒ 渲染断言会变得不可靠 |
 *
 * ## 🔴 三个"只读"字段（不是偷懒，是三条铁律的界面表达）
 *
 * | 字段 | 编辑态 | 为什么 |
 * |---|---|---|
 * | **类型** | 只读文本 | 一张储蓄卡不会"变成"信用卡（契约层 `UpdateAccountRequest` 就没有 `type`）⇒ 给一个能改的下拉等于**给了一个点不动的控件** |
 * | **归属人** | 只读文本 | 改归属人走专用路由 `PUT /accounts/:id/owner`（仅 owner），**不是"编辑字段"而是另一个管理动作** ⇒ 本批不画该入口（它需要成员列表 + owner 判定，属另一批） |
 * | **初始余额** | 只读文本 | 🔴 `AC-B6.7` 红线：**无「直接改账户余额」路径**。事后校准的唯一手段是「调整」流水 |
 *
 * 🔴 **只读字段用 `TextField` + `InputProps.readOnly` 而不是纯文本**：用户看到的仍是
 * "一个字段在那儿"，只是改不动 —— 比"字段突然消失"更少困惑（表单布局也不跳）。
 *
 * ## 本组件**不做**的事
 *
 * - **不校验、不算金额** —— 全部在 `buildAccountRequest()`（语义部分来自内核 `checkAccountShape`）；
 * - **不自己编错误文案** —— `errors` 由调用方传入，服务端 `message` 也**原样展示**；
 * - **不持有状态、不发请求** —— 提交由调用方处理。
 *
 * ## `internal` 品类与 `contra` 事项类型是**互斥**的（架构 §4.2）
 *
 * 二者的可见性都由 `type` 决定：`internal` 只露品类、`contra` 只露事项类型、其余两类都不露。
 * 🔴 **这是"不给出犯错机会"，不是校验** —— 草稿层还会把非本类型的残留值归零
 * （见 `account-draft.ts` 的 `shapeOf()`），即便有人改了组件也不会把脏值送进请求。
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { AccountPolicy, ContraKind, InternalCategory, currencySymbolOf } from '@jiaying/shared';
import type { ChangeEvent } from 'react';
import type { AccountDraft } from '../features/account-draft.js';
import {
  ACCOUNT_TYPE_OPTIONS,
  CONTRA_KIND_OPTIONS,
  INTERNAL_CATEGORY_OPTIONS,
  LOAN_SUBTYPE_OPTIONS,
  REPAYMENT_METHOD_OPTIONS,
  accountTypeLabel,
  contraKindLabel,
  internalCategoryLabel,
} from '../features/account-labels.js';

export interface AccountFormProps {
  draft: AccountDraft;
  /** 任一字段变化 —— 由调用方合并（本组件不持有状态） */
  onChange: (patch: Partial<AccountDraft>) => void;
  /** 本地或服务端的问题清单（**原样展示**，不重写文案） */
  errors: readonly string[];
  /** 提交中（禁用按钮，防重复提交） */
  busy: boolean;
  mode: 'create' | 'edit';
  /**
   * 编辑态的**只读**展示值（归属人 / 初始余额 / 信用卡类型名）。
   * ⚠️ 这些字段不进请求（除类型外 —— 它压根不在 `UpdateAccountRequest` 里），
   * 故不由 `draft` 承载显示而是由调用方从服务端视图直接给。
   */
  readonlyView?: {
    readonly ownerName: string;
    readonly initialBalanceText: string;
  };
  onSubmit: () => void;
  onCancel: () => void;
}

export function AccountForm({
  draft,
  onChange,
  errors,
  busy,
  mode,
  readonlyView,
  onSubmit,
  onCancel,
}: AccountFormProps) {
  const isCreate = mode === 'create';
  const sym = currencySymbolOf(draft.currency);
  const set = (key: keyof AccountDraft) => (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ [key]: e.target.value } as Partial<AccountDraft>);
  };

  const isInternal = draft.type === 'internal';
  const isContra = draft.type === 'contra';
  const isCredit = draft.type === 'credit';
  /** 🆕 次卡只对预付卡有语义（`DR-057` D4 · 内核第 13 条同一条规则） */
  const isPrepaid = isInternal && draft.category === InternalCategory.Prepaid;
  /** 🆕 贷款参数只在「往来款项 + 贷款」上出现（`DR-044`；判定唯一处 = 内核 `checkAccountShape`） */
  const isLoan = isContra && draft.contraKind === ContraKind.Loan;

  return (
    <Card data-testid="account-form">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {isCreate ? '新建账户' : '编辑账户'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          余额是派生值（初始余额 + 所有笔）· 由服务端算好下发，端侧不重算
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="account-form-errors">
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Stack>
          </Alert>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={1.5}>
          {/* ── ① 标识：名称 + 类型 ── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
            <TextField
              label="账户名"
              required
              value={draft.name}
              onChange={set('name')}
              inputProps={{ 'data-testid': 'name', maxLength: AccountPolicy.NameMaxLength }}
              // 🔴 `DR-022` §2.1：往来款项的 `name` 承载**事项**（如「借小王-装修」），对手方另有字段
              helperText={
                isContra
                  ? `写「事项」（如「借小王-装修」）；对手方在下面单独填 · 不超过 ${AccountPolicy.NameMaxLength} 个字符`
                  : `不超过 ${AccountPolicy.NameMaxLength} 个字符`
              }
            />
            {isCreate ? (
              <TextField
                select
                label="类型"
                required
                value={draft.type}
                onChange={set('type')}
                inputProps={{ 'data-testid': 'type' }}
                helperText="建好后不可改（一张卡不会变成另一种卡）"
              >
                {ACCOUNT_TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="类型"
                value={accountTypeLabel(draft.type)}
                InputProps={{ readOnly: true }}
                inputProps={{ 'data-testid': 'type-readonly' }}
                helperText="不可修改 —— 需要换形态请删除后重建"
              />
            )}
          </Box>

          {/* ── ② 形态分支：internal 品类 OR contra 事项类型（互斥 · 架构 §4.2）── */}
          {isInternal && (
            <TextField
              select
              label="内部品类"
              required
              value={draft.category}
              onChange={set('category')}
              inputProps={{ 'data-testid': 'category' }}
              helperText="决定「可否欠着」与「何时计支出」—— 由品类决定，不可另设开关"
            >
              {INTERNAL_CATEGORY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isContra && (
            <TextField
              select
              label="事项类型"
              required
              value={draft.contraKind}
              onChange={set('contraKind')}
              inputProps={{ 'data-testid': 'contraKind' }}
              helperText="一条往来关系 = 一个账户；本金往来不计收支"
            >
              {CONTRA_KIND_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isContra && (
            <TextField
              label="对手方"
              // 🔴 **必填**（`DR-047` D8 · `Q域PRD` §7 #1："对手方是「关系」的标识，缺失即拒绝保存"）
              required
              value={draft.counterparty}
              onChange={set('counterparty')}
              inputProps={{ 'data-testid': 'counterparty', maxLength: AccountPolicy.CounterpartyMaxLength }}
              helperText="跟谁往来（如「小王」）· 必填；账户名写事项（如「借小王-装修」）"
            />
          )}
          {isInternal && draft.category !== '' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              当前品类：<b>{internalCategoryLabel(draft.category)}</b>
            </Typography>
          )}
          {isContra && draft.contraKind !== '' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              当前事项：<b>{contraKindLabel(draft.contraKind)}</b>
            </Typography>
          )}

          {/* ── ③ 归属人 ── */}
          {isCreate ? (
            <TextField
              label="归属人"
              value={draft.ownerId}
              onChange={set('ownerId')}
              inputProps={{ 'data-testid': 'ownerId' }}
              helperText="留空 = 归当前用户（服务端缺省）· 建好后改归属人需 owner"
            />
          ) : (
            <TextField
              label="归属人"
              value={readonlyView?.ownerName ?? ''}
              InputProps={{ readOnly: true }}
              inputProps={{ 'data-testid': 'owner-readonly' }}
              helperText="改归属人是管理动作（仅创建者可用），不在本表单"
            />
          )}

          {/* ── ④ 币种 + 初始余额 ── */}
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
            <TextField
              label="币种"
              required
              value={draft.currency}
              onChange={set('currency')}
              inputProps={{ 'data-testid': 'currency', maxLength: 3 }}
              helperText="ISO 4217 三字母（如 CNY / USD）· 余额永不折算"
            />
            {isCreate ? (
              <TextField
                label={`初始余额（${sym}）`}
                value={draft.initialBalanceText}
                onChange={set('initialBalanceText')}
                inputProps={{ 'data-testid': 'initialBalanceText', inputMode: 'decimal' }}
                helperText="录入当期的事实值；之后只能用「调整」笔校准"
              />
            ) : (
              <TextField
                label={`初始余额（${sym}）`}
                value={readonlyView?.initialBalanceText ?? ''}
                InputProps={{ readOnly: true }}
                inputProps={{ 'data-testid': 'initialBalance-readonly' }}
                helperText="不可直接修改 —— 事后校准请记一笔「调整」"
              />
            )}
          </Box>

          {/* ── ⑤ 信用卡四字段（仅 credit）── */}
          {isCredit && (
            <>
              <Divider sx={{ mt: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                信用卡信息（可选，用于账单提醒与使用率显示）
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
                <TextField
                  label={`信用额度（${sym}）`}
                  value={draft.creditLimitText}
                  onChange={set('creditLimitText')}
                  inputProps={{ 'data-testid': 'creditLimitText', inputMode: 'decimal' }}
                  helperText="留空则不显示使用率进度条"
                />
                <TextField
                  label="卡号后四位"
                  value={draft.tailNumber}
                  onChange={set('tailNumber')}
                  inputProps={{ 'data-testid': 'tailNumber', maxLength: 4 }}
                  helperText="仅展示用 —— 不存完整卡号"
                />
                {/*
                  🔴 **账单日 / 还款日是「每月几号」（1–28 的序号），不是日期** ——
                  批 `115`（T4）明确**不许**改成 `type="date"`：那会把它存成一个"具体某天"
                  （如 2026-10-05），而银行账单是**每月循环**的。
                  这里只补 `min` / `max` 把可输入范围收在 1–28（与 `AccountPolicy` 同源）。
                */}
                <TextField
                  label="账单日"
                  value={draft.billingDayText}
                  onChange={set('billingDayText')}
                  inputProps={{
                    'data-testid': 'billingDayText',
                    inputMode: 'numeric',
                    min: AccountPolicy.BillingDayMin,
                    max: AccountPolicy.BillingDayMax,
                  }}
                  helperText={`${AccountPolicy.BillingDayMin}–${AccountPolicy.BillingDayMax} 日，与还款日成对`}
                />
                <TextField
                  label="还款日"
                  value={draft.repaymentDayText}
                  onChange={set('repaymentDayText')}
                  inputProps={{
                    'data-testid': 'repaymentDayText',
                    inputMode: 'numeric',
                    min: AccountPolicy.BillingDayMin,
                    max: AccountPolicy.BillingDayMax,
                  }}
                  helperText={`${AccountPolicy.BillingDayMin}–${AccountPolicy.BillingDayMax} 日，与账单日成对`}
                />
              </Box>
            </>
          )}

          {/* ── ⑤-1.5 🆕 预付卡次卡（仅 internal + prepaid；`DR-057` D4）── */}
          {isPrepaid && (
            <>
              <Divider sx={{ mt: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                次卡（可选）—— 登记后每次点「用一次」自动扣一次单次金额；
                <b>两项要么都填、要么都留空</b>
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
                <TextField
                  label="总次数"
                  value={draft.passTotalText}
                  onChange={set('passTotalText')}
                  inputProps={{ 'data-testid': 'passTotalText', inputMode: 'numeric' }}
                  helperText={
                    isCreate
                      ? '这张卡一共能用几次'
                      : '已用次数不能手工改 —— 只能通过「用一次」累加，防止剩余次数凭空变多'
                  }
                />
                <TextField
                  label={`单次金额（${sym}）`}
                  value={draft.passUnitAmountText}
                  onChange={set('passUnitAmountText')}
                  inputProps={{ 'data-testid': 'passUnitAmountText', inputMode: 'decimal' }}
                  helperText="每次扣多少 —— 全卡一个价"
                />
              </Box>
            </>
          )}

          {/* ── ⑤-2 🆕 期限块（全部 contra 事项；`DR-044`）── */}
          {isContra && (
            <>
              <Divider sx={{ mt: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                期限与贷款信息（**全部可留空** —— 只知道"还剩几期"也能先记着）
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
                <TextField
                  label="到期日"
                  type="date"
                  value={draft.dueDateText}
                  onChange={set('dueDateText')}
                  // 🆕 批 `115`（T4）：原生日期控件（有日历），仍可键入
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 'data-testid': 'dueDateText' }}
                  helperText="约定还款 / 收回的日期；无固定期限可留空"
                />
                {isLoan && (
                  <>
                    <TextField
                      label="年利率（%）"
                      value={draft.annualRatePercentText}
                      onChange={set('annualRatePercentText')}
                      inputProps={{ 'data-testid': 'annualRatePercentText', inputMode: 'decimal' }}
                      helperText="百分数，最多两位小数（如 3.36）；留空 = 未知"
                    />
                    <TextField
                      label="总期数"
                      value={draft.termsText}
                      onChange={set('termsText')}
                      inputProps={{ 'data-testid': 'termsText', inputMode: 'numeric' }}
                      helperText="总期数（非剩余）；剩余由计划表数出"
                    />
                    <TextField
                      label="起息日"
                      type="date"
                      value={draft.startDateText}
                      onChange={set('startDateText')}
                      // 🆕 批 `115`（T4）：原生日期控件（有日历），仍可键入
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'data-testid': 'startDateText' }}
                      helperText="放款 / 起算利息的那天"
                    />
                    <TextField
                      select
                      label="还款方式"
                      value={draft.method}
                      onChange={set('method')}
                      inputProps={{ 'data-testid': 'method' }}
                      helperText="决定后续期次怎么重排"
                    >
                      <MenuItem value="">（未填）</MenuItem>
                      {REPAYMENT_METHOD_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="贷款类型"
                      value={draft.subtype}
                      onChange={set('subtype')}
                      inputProps={{ 'data-testid': 'subtype' }}
                      helperText="房贷 / 车贷 / 消费贷 / 其他"
                    >
                      <MenuItem value="">（未填）</MenuItem>
                      {LOAN_SUBTYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </>
                )}
              </Box>
            </>
          )}

          {/* ── ⑥ 收起开关（社保账户默认收起）── */}
          <FormControlLabel
            control={
              <Switch
                checked={draft.hidden}
                onChange={(_e, checked) => onChange({ hidden: checked })}
              />
            }
            label="在账户页收起（社保 / 内部账户默认收起）"
          />
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={onCancel} disabled={busy} data-testid="account-cancel">
            取消
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="account-submit">
            {busy ? '提交中…' : isCreate ? '新建' : '保存'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
